import type { ReactNode } from 'react';
import { CircleAlert, ListChecks } from 'lucide-react';
import { Card, EmptyState, LoadMore, StatusPill, TableSkeleton } from '@/components/ui';
import { useSiteJobHistory } from '@/hooks/useJobs';
import { errorText, failureDetails, failureText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { JOB_STATE, JOB_TYPE_LABELS, label } from '@/utils/labels';

/** Every job the site has had, step by step, while the jobs are kept. */
export function HistoryTab({ siteId }: { readonly siteId: string }): ReactNode {
  const query = useSiteJobHistory(siteId);

  if (query.isPending) {
    return (
      <Card flush>
        <TableSkeleton rows={4} columns={4} />
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(query.error, 'История не загрузилась')}
        </p>
      </Card>
    );
  }

  const jobs = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <Card flush>
        {jobs.length === 0 ? (
          <EmptyState icon={ListChecks} message="С этим сайтом ещё ничего не делали" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Операция</th>
                  <th scope="col">Состояние</th>
                  <th scope="col">Когда</th>
                  <th scope="col" className="numeric">
                    Попыток
                  </th>
                  <th scope="col">Причина отказа</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td title={job.type}>{label(JOB_TYPE_LABELS, job.type)}</td>
                    <td>
                      <StatusPill map={JOB_STATE} status={job.state} />
                    </td>
                    <td className="muted nowrap" title={job.createdAt}>
                      {formatRelative(job.createdAt)}
                    </td>
                    <td className="numeric">{job.attempts}</td>
                    <td>
                      {job.errorCode ? (
                        <div className="stack stack-tight">
                          <span className="tone-down" title={job.errorCode}>
                            {failureText(job.errorCode)}
                          </span>
                          {failureDetails(job.errorDetails).map((line) => (
                            <span key={line} className="text-sm muted">
                              {line}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <LoadMore
        shown={jobs.length}
        total={query.data?.pages[0]?.total ?? null}
        hasMore={query.hasNextPage}
        busy={query.isFetchingNextPage}
        onMore={() => void query.fetchNextPage()}
      />
    </>
  );
}
